<template>
  <!-- Enhanced Footer -->
  <v-footer class="myFooter" elevation="8">
    <v-container>
      <v-row>
        <v-col cols="12" md="4" class="footer-brand">
          <div class="brand-content">
            <h3 class="brand-name">Thomas Antwi</h3>
            <p class="brand-tagline">Crafting Digital Excellence</p>
            <p class="brand-description">
              Passionate about creating innovative web solutions that combine
              beautiful design with powerful functionality.
            </p>
          </div>
        </v-col>

        <v-col cols="12" md="4" class="footer-links">
          <h4 class="footer-section-title">Quick Navigation</h4>
          <div class="quick-links-grid">
            <a
              v-for="item in navigationItems"
              :key="item.key"
              @click="$emit('navigate', item.name, item.key)"
              class="quick-link-item"
            >
              <v-icon small class="link-icon">{{ item.icon }}</v-icon>
              {{ item.name }}
            </a>
          </div>
        </v-col>

        <v-col cols="12" md="4" class="footer-social">
          <h4 class="footer-section-title">Let's Connect</h4>
          <div class="social-links">
            <v-btn
              v-for="(social, index) in socialMedia"
              :key="index"
              icon
              large
              class="social-btn"
              :style="{ '--social-color': social.color }"
              @click="openLink(social.url)"
            >
              <v-icon>{{ social.icon }}</v-icon>
            </v-btn>
          </div>
          <p class="footer-contact-text">Ready to start your project?</p>
          <v-btn
            color="primary"
            rounded
            small
            class="contact-btn"
            @click="$emit('show-contact')"
          >
            Get In Touch
          </v-btn>
        </v-col>
      </v-row>

      <v-divider class="footer-divider"></v-divider>

      <div class="footer-bottom">
        <div class="copyright-section">
          <span class="copyright-text">
            © {{ currentYear }} Thomas Antwi. Crafted with passion.
          </span>
          <span class="tech-stack">
            Built with <v-icon small color="success">mdi-vuejs</v-icon> Vue.js &
            <v-icon small color="primary">mdi-vuetify</v-icon> Vuetify
          </span>
        </div>
      </div>
    </v-container>
  </v-footer>
</template>

<script>
export default {
  name: "Footer",
  props: {
    navigationItems: {
      type: Array,
      required: true,
      default: () => []
    },
    socialMedia: {
      type: Array,
      required: true,
      default: () => []
    }
  },
  computed: {
    currentYear() {
      return new Date().getFullYear();
    }
  },
  methods: {
    openLink(url) {
      window.open(url, '_blank');
    }
  }
};
</script>

<style lang="scss" scoped>
@import "@/styles/variables.scss";

/* Enhanced Footer */
.myFooter {
  background: linear-gradient(
    135deg,
    rgba(10, 10, 10, 0.95) 0%,
    rgba(26, 26, 46, 0.95) 50%,
    rgba(22, 33, 62, 0.95) 100%
  ) !important;
  backdrop-filter: blur(20px);
  border-top: 1px solid rgba(67, 97, 238, 0.2);
  padding: 4rem 0 2rem !important;
  margin-top: 4rem;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, rgba(67, 97, 238, 0.05), rgba(72, 149, 239, 0.05));
    z-index: 0;
  }

  .v-container {
    position: relative;
    z-index: 1;
  }

  &.theme--light {
    background: linear-gradient(
      135deg,
      rgba(248, 249, 250, 0.95) 0%,
      rgba(240, 242, 247, 0.95) 50%,
      rgba(233, 236, 239, 0.95) 100%
    ) !important;
    border-top-color: rgba(67, 97, 238, 0.15);

    &::before {
      background: linear-gradient(135deg, rgba(67, 97, 238, 0.03), rgba(72, 149, 239, 0.03));
    }
  }
}

.footer-brand {
  .brand-name {
    font-size: 2rem;
    font-weight: 800;
    background: linear-gradient(135deg, $primary, $accent);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    margin-bottom: 0.5rem;
    font-family: 'Poppins', sans-serif;
    text-shadow: 0 2px 10px rgba(67, 97, 238, 0.3);
  }

  .brand-tagline {
    font-size: 1.1rem;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.9);
    margin-bottom: 1rem;

    .theme--light & {
      color: rgba(33, 37, 41, 0.8);
    }
  }

  .brand-description {
    color: rgba(255, 255, 255, 0.7);
    line-height: 1.6;
    font-size: 0.95rem;

    .theme--light & {
      color: rgba(73, 80, 87, 0.8);
    }
  }
}

.footer-section-title {
  font-size: 1.3rem;
  font-weight: 700;
  color: #ffffff;
  margin-bottom: 2rem;
  position: relative;
  text-shadow: 0 2px 10px rgba(67, 97, 238, 0.3);

  .theme--light & {
    color: rgba(33, 37, 41, 0.9);
    text-shadow: none;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: -8px;
    left: 0;
    width: 40px;
    height: 3px;
    background: linear-gradient(135deg, $primary, $accent);
    border-radius: 2px;
    box-shadow: 0 2px 10px rgba(67, 97, 238, 0.3);
  }
}

.quick-links-grid {
  display: grid;
  gap: 1rem;
}

.quick-link-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  color: rgba(255, 255, 255, 0.8);
  text-decoration: none;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  cursor: pointer;
  padding: 0.75rem 1rem;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);

  .theme--light & {
    color: rgba(73, 80, 87, 0.8);
    background: rgba(255, 255, 255, 0.7);
    border-color: rgba(67, 97, 238, 0.1);
  }

  &:hover {
    color: #ffffff;
    transform: translateY(-3px);
    background: rgba(67, 97, 238, 0.2);
    border-color: rgba(67, 97, 238, 0.4);
    box-shadow: 0 10px 25px rgba(67, 97, 238, 0.2);

    .theme--light & {
      color: $primary;
      background: rgba(67, 97, 238, 0.1);
      box-shadow: 0 10px 25px rgba(67, 97, 238, 0.15);
    }

    .link-icon {
      color: $primary !important;
      transform: scale(1.2) rotate(5deg);
    }
  }

  .link-icon {
    transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
    color: rgba(255, 255, 255, 0.6);

    .theme--light & {
      color: rgba(73, 80, 87, 0.6);
    }
  }
}

.footer-social {
  .social-links {
    display: flex;
    gap: 1.5rem;
    margin-bottom: 2rem;
    justify-content: flex-start;
    flex-wrap: wrap;
  }

  .social-btn {
    background: rgba(255, 255, 255, 0.05) !important;
    border: 2px solid rgba(255, 255, 255, 0.1);
    border-radius: 16px !important;
    backdrop-filter: blur(10px);
    width: 56px;
    height: 56px;
    transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);

    .theme--light & {
      background: rgba(255, 255, 255, 0.8) !important;
      border-color: rgba(67, 97, 238, 0.1);
    }

    .v-icon {
      color: var(--social-color) !important;
      transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
      font-size: 24px;
    }

    &:hover {
      background: var(--social-color) !important;
      border-color: var(--social-color) !important;
      transform: translateY(-5px) scale(1.05);
      box-shadow: 0 15px 30px rgba(var(--social-color), 0.4);

      .v-icon {
        color: white !important;
        transform: scale(1.2) rotate(5deg);
      }
    }
  }

  .footer-contact-text {
    color: rgba(255, 255, 255, 0.8);
    margin-bottom: 1.5rem;
    font-weight: 500;
    font-size: 1.1rem;

    .theme--light & {
      color: rgba(73, 80, 87, 0.8);
    }
  }

  .contact-btn {
    background: linear-gradient(135deg, $primary, $accent) !important;
    color: white !important;
    font-weight: 600;
    text-transform: none;
    border-radius: 16px !important;
    padding: 12px 32px !important;
    font-size: 1rem;
    box-shadow: 0 8px 20px rgba(67, 97, 238, 0.3);
    transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);

    &:hover {
      transform: translateY(-3px) scale(1.02);
      box-shadow: 0 15px 35px rgba(67, 97, 238, 0.4);
      background: linear-gradient(135deg, $accent, $primary) !important;
    }
  }
}

.footer-divider {
  margin: 3rem 0 2rem;
  background: linear-gradient(90deg, transparent, rgba(67, 97, 238, 0.3), transparent) !important;
  height: 2px !important;
  border-radius: 1px;
  box-shadow: 0 2px 10px rgba(67, 97, 238, 0.2);

  .theme--light & {
    background: linear-gradient(90deg, transparent, rgba(67, 97, 238, 0.2), transparent) !important;
  }
}

.footer-bottom {
  .copyright-section {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 1.5rem;
    padding: 1.5rem 2rem;
    background: rgba(255, 255, 255, 0.05);
    border-radius: 16px;
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.1);

    .theme--light & {
      background: rgba(255, 255, 255, 0.7);
      border-color: rgba(67, 97, 238, 0.1);
    }

    @media (max-width: $breakpoint-xs) {
      flex-direction: column;
      text-align: center;
      padding: 1rem;
    }
  }

  .copyright-text,
  .tech-stack {
    color: rgba(255, 255, 255, 0.8);
    font-size: 0.95rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-weight: 500;

    .theme--light & {
      color: rgba(73, 80, 87, 0.8);
    }

    .v-icon {
      filter: drop-shadow(0 2px 4px rgba(67, 97, 238, 0.3));
    }
  }
}

// Responsive Design
@media (max-width: $breakpoint-xs) {
  .myFooter {
    padding: 3rem 0 1.5rem !important;

    .footer-brand .brand-name {
      font-size: 1.7rem;
    }

    .footer-section-title {
      font-size: 1.2rem;
      margin-bottom: 1.5rem;
    }

    .social-links {
      justify-content: center;
      gap: 1rem;
    }

    .social-btn {
      width: 48px;
      height: 48px;

      .v-icon {
        font-size: 20px;
      }
    }

    .quick-link-item {
      padding: 0.5rem 0.75rem;
      font-size: 0.9rem;
    }

    .copyright-section {
      padding: 1rem;
      gap: 1rem;
    }
  }
}
</style>